'use client';

// src/app/error.tsx
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Home, RefreshCw, MessageCircle, AlertTriangle } from 'lucide-react';
import { useSession } from 'next-auth/react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const { data: session } = useSession();
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    additionalDetails: '',
  });

  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Application error:', error);
  }, [error]);

  // Check if user is admin
  const isAdmin = (session?.user as any)?.role === 'admin' || session?.user?.email?.toLowerCase().includes('admin');

  // Check if it's a network/fetch error
  const isNetworkError = error.message?.toLowerCase().includes('fetch') ||
                         error.message?.toLowerCase().includes('network') ||
                         error.message?.toLowerCase().includes('503') ||
                         error.message?.toLowerCase().includes('504');

  const handleAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create bug report via API
      const response = await fetch('/api/proxy/api/bug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title || `500 Error: ${error.message?.substring(0, 50)}`,
          description: formData.additionalDetails || error.message || 'No description provided',
          severity: 'High',
          component: 'Frontend',
          environment: 'Production',
          stackTrace: error.stack || '',
          reproductionSteps: `Error occurred on page: ${typeof window !== 'undefined' ? window.location.href : 'unknown'}\nError Digest: ${error.digest || 'N/A'}`,
          browserInfo: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setShowAdminForm(false);
          setSubmitted(false);
        }, 3000);
      } else {
        alert('Failed to submit error report');
      }
    } catch (err) {
      console.error('Failed to submit error report:', err);
      alert('Failed to submit error report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-16">
      <div className="text-center max-w-2xl mx-auto">
        {/* Error icon/code */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-destructive/10 mb-4">
            <svg
              className="w-12 h-12 text-destructive"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-6xl font-bold text-destructive/20 select-none">
            {isNetworkError ? '503' : '500'}
          </h1>
        </div>

        {/* Error message */}
        <div className="space-y-4 mb-8">
          <h2 className="text-3xl font-bold">
            {isNetworkError ? 'Service Temporarily Unavailable' : 'Something Went Wrong'}
          </h2>
          <p className="text-muted-foreground text-lg">
            {isNetworkError
              ? "We're experiencing technical difficulties. Our team is working to restore service. Please try again in a few moments."
              : "We're sorry, but something unexpected happened. Don't worry, we've been notified and are working on a fix."}
          </p>

          {error.digest && (
            <p className="text-xs text-muted-foreground font-mono bg-muted px-3 py-2 rounded inline-block">
              Error ID: {error.digest}
            </p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            <RefreshCw className="w-5 h-5" />
            Try Again
          </button>

          <Link
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors font-medium"
          >
            <Home className="w-5 h-5" />
            Go Home
          </Link>

          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 border border-input rounded-lg hover:bg-accent hover:text-accent-foreground transition-colors font-medium"
          >
            <MessageCircle className="w-5 h-5" />
            Contact Support
          </Link>
        </div>

        {/* Admin Error Reporting */}
        {isAdmin && (
          <div className="mt-8 pt-8 border-t border-border">
            {!showAdminForm ? (
              <button
                onClick={() => setShowAdminForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
              >
                <AlertTriangle className="w-4 h-4" />
                Report This Error (Admin)
              </button>
            ) : (
              <div className="bg-card border rounded-lg p-6 text-left max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Admin Error Report</h3>
                  <button
                    onClick={() => setShowAdminForm(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                </div>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded p-4 text-green-800">
                    ✓ Error report submitted successfully!
                  </div>
                ) : (
                  <form onSubmit={handleAdminSubmit} className="space-y-4">
                    {/* Auto-captured info */}
                    <div className="bg-muted p-3 rounded text-xs space-y-1">
                      <div><strong>Error:</strong> {error.message}</div>
                      {error.digest && <div><strong>Digest:</strong> {error.digest}</div>}
                      <div><strong>Page:</strong> {typeof window !== 'undefined' ? window.location.href : 'unknown'}</div>
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Bug Title (optional)
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Brief description of what you were doing"
                        className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-ring"
                        disabled={submitting}
                      />
                    </div>

                    {/* Additional Details */}
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Additional Details (optional)
                      </label>
                      <textarea
                        value={formData.additionalDetails}
                        onChange={(e) => setFormData({ ...formData, additionalDetails: e.target.value })}
                        placeholder="What were you doing when this error occurred?"
                        rows={3}
                        className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-ring"
                        disabled={submitting}
                      />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 text-sm font-medium disabled:opacity-50"
                      >
                        {submitting ? 'Submitting...' : 'Submit Error Report'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAdminForm(false)}
                        disabled={submitting}
                        className="px-4 py-2 border rounded hover:bg-accent text-sm disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        )}

        {/* Additional info */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-12 pt-8 border-t border-border">
            <details className="text-left">
              <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground mb-2">
                Error Details (Development Only)
              </summary>
              <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-48">
                {error.message}
                {'\n\n'}
                {error.stack}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
